import { Button } from '@factorialco/deprecated-design-system'
import {
  PayrollRunInfoPayrollRunFragment,
  TogglePayrollRunLockDocument,
} from 'generated/resources/graphql'
import { CompensationsPayrollRunDocument } from 'generated/resources/graphql'
import { CycleRouteMetaDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { PayrollRunStatus } from 'modules/compensations/lib/payrollRunStatus'
import {
  getDaysSinceCycleStart,
  trackCycleStatusChanged,
} from 'modules/compensations/lib/cycleStatusTracking'

type PayrollRunInfoProps = PayrollRunInfoPayrollRunFragment
interface TogglePayrollRunLockModalProps {
  payrollRun: PayrollRunInfoProps
  companyId: number
  country: string
  lock: boolean
  onSuccess: () => void
  onClose: () => void
}

const TogglePayrollRunLockModal = ({
  payrollRun,
  companyId,
  country,
  onSuccess,
  onClose,
  lock,
}: TogglePayrollRunLockModalProps) => {
  const [togglePayrollRunLock] = useMutation(TogglePayrollRunLockDocument, {
    extractorFn: (result) => result.compensationsMutations.toggleLockCompensationsPayrollRun,
  })

  const onSubmit = async () => {
    const result = await togglePayrollRunLock({
      variables: {
        lock,
        payrollRunId: payrollRun.id,
      },
      refetchQueries: [CompensationsPayrollRunDocument, CycleRouteMetaDocument],
    })

    if (lock) {
      track(trackingEvents.compensations.lockPayrollRun, {
        payrollRunId: payrollRun.id,
      })
    } else {
      track(trackingEvents.compensations.unlockPayrollRun, {
        payrollRunId: payrollRun.id,
      })
    }

    const handled = handleResult(result, () => undefined)

    if (handled.success && handled.data.payrollRun) {
      if (lock) {
        const previousStatus = payrollRun.status || PayrollRunStatus.OPEN
        trackCycleStatusChanged({
          cycle_id: String(payrollRun.id),
          company_id: String(companyId),
          previous_status: previousStatus,
          new_status: PayrollRunStatus.IN_PREPARATION,
          triggered_by_action: 'lock',
          cycle_period_start: payrollRun.startsOn,
          cycle_period_end: payrollRun.endsOn,
          days_since_cycle_creation: getDaysSinceCycleStart(payrollRun.startsOn),
          country,
        })
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onSuccess()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }

    return handled
  }

  // FIXME: Once i18nDateToString is ressurected move the format to the i18n translations
  const formatedStartsOn = stringToDate(payrollRun.startsOn, 'backend_datetime').format('MMMM YYYY')

  const title = lock
    ? i18n.t('compensations.PayrollRunCompensationsList.lock_modal_lock_title', {
        period: formatedStartsOn,
      })
    : i18n.t('compensations.PayrollRunCompensationsList.lock_modal_unlock_title', {
        period: formatedStartsOn,
      })
  const description = lock
    ? i18n.t('compensations.PayrollRunCompensationsList.lock_modal_lock_description')
    : i18n.t('compensations.PayrollRunCompensationsList.lock_modal_unlock_description')

  return (
    <Modal
      illustration="permissions"
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.confirm')}</Button>
        </>
      }
    ></Modal>
  )
}

export default TogglePayrollRunLockModal
